/**
 * ARCHIVE COMMAND — the operating system of The Archive.
 * Global palette (Ctrl/⌘+K): universal search, founder commands, intents,
 * intelligence dashboard, favorites, terminal-style history, workspaces.
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import {
  Folder, Network, FileText, Image as ImageIcon, User, Shield, ShieldAlert,
  Terminal, Sparkles, Sun, Zap, Clock, Link2, MessageSquare, ArrowRight,
  Activity, Radar, Cpu, CheckSquare, Inbox, Mail, BarChart, Eye, Settings,
  Users, Database, Download, KeyRound, PenLine, Copy, Layers, Pin, ScrollText,
  FolderOpen, Fingerprint, Search,
} from "lucide-react";
import { contextActionsFor, pageContextFor } from "./registry";
import { api, track as apiTrack } from "./commandApi";
import OracleInline from "./OracleInline";
import WorkspaceBuilder from "./WorkspaceBuilder";
import CallsignEditor from "./CallsignEditor";
import MorningGreeting from "./MorningGreeting";
import AlertCenter from "./AlertCenter";
import { useCallsign, refreshCallsign } from "./useCallsign";

const ICONS = {
  folder: Folder, network: Network, "file-text": FileText, image: ImageIcon,
  user: User, shield: Shield, "shield-alert": ShieldAlert, terminal: Terminal,
  sparkles: Sparkles, sun: Sun, zap: Zap, clock: Clock, link: Link2,
  "message-square": MessageSquare, "arrow-right": ArrowRight, activity: Activity,
  radar: Radar, cpu: Cpu, "check-square": CheckSquare, inbox: Inbox, mail: Mail,
  "bar-chart": BarChart, eye: Eye, settings: Settings, users: Users,
  database: Database, download: Download, "key-round": KeyRound,
  "pen-line": PenLine, copy: Copy, layers: Layers, scroll: ScrollText,
  "folder-open": FolderOpen, fingerprint: Fingerprint, file: FileText,
};
const Icon = ({ name, size = 13, className = "" }) => {
  const C = ICONS[name] || FileText;
  return <C size={size} className={className} />;
};

export default function CommandPalette() {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");
  const [results, setResults] = useState(null);
  const [dash, setDash] = useState(null);
  const [recs, setRecs] = useState([]);
  const [alerts, setAlerts] = useState([]);
  const [watchlist, setWatchlist] = useState([]);
  const [mode, setMode] = useState("search");   // search | oracle | builder
  const [sel, setSel] = useState(0);
  const [histIdx, setHistIdx] = useState(-1);
  const [wsOpen, setWsOpen] = useState(null);
  const inputRef = useRef(null);
  const listRef = useRef(null);
  const oracleRef = useRef(null);
  const identity = useCallsign();
  const navigate = useNavigate();
  const location = useLocation();

  const prevFocusRef = useRef(null);

  /* global shortcut + trigger event */
  useEffect(() => {
    const onKey = (e) => {
      if ((e.ctrlKey || e.metaKey) && !e.altKey && e.key.toLowerCase() === "k") {
        e.preventDefault();
        setOpen((v) => !v);
      }
    };
    const onOpenEvent = () => setOpen(true);
    window.addEventListener("keydown", onKey);
    window.addEventListener("archive:command-open", onOpenEvent);
    return () => {
      window.removeEventListener("keydown", onKey);
      window.removeEventListener("archive:command-open", onOpenEvent);
    };
  }, []);

  /* Escape: leave oracle/builder first, then close (never interferes with
     other overlays) */
  const modeRef = useRef(mode);
  modeRef.current = mode;
  useEffect(() => {
    if (!open) return undefined;
    const onEsc = (e) => {
      if (e.key !== "Escape") return;
      e.stopPropagation();
      if (modeRef.current !== "search") setMode("search");
      else setOpen(false);
    };
    window.addEventListener("keydown", onEsc, true);
    return () => window.removeEventListener("keydown", onEsc, true);
  }, [open]);

  useEffect(() => {
    if (open) {
      prevFocusRef.current = document.activeElement;
      setQ(""); setResults(null); setSel(0); setHistIdx(-1); setWsOpen(null);
      setMode("search");
      setTimeout(() => inputRef.current?.focus(), 10);
      refreshCallsign();
      api("/command/dashboard").then((d) => {
        setDash(d);
        if (d.level >= 1) {
          api("/command/watchlist")
            .then((w) => setWatchlist(w.watchlist || [])).catch(() => setWatchlist([]));
        }
        if (d.level >= 2) {
          api("/command/recommendations")
            .then((r) => setRecs(r.recommendations || []))
            .catch(() => setRecs([]));
          api("/command/alerts")
            .then((a) => setAlerts(a.alerts || [])).catch(() => setAlerts([]));
        }
      }).catch(() => setDash({}));
    } else if (prevFocusRef.current) {
      const el = prevFocusRef.current;
      prevFocusRef.current = null;
      if (el && typeof el.focus === "function" && document.contains(el)) el.focus();
    }
  }, [open]);

  /* instant search */
  useEffect(() => {
    if (!open || mode !== "search") return undefined;
    if (!q.trim()) { setResults(null); return undefined; }
    const t = setTimeout(() => {
      api(`/command/search?q=${encodeURIComponent(q)}`)
        .then((d) => { setResults(d); setSel(0); })
        .catch(() => setResults({ results: [], intents: [] }));
    }, 120);
    return () => clearTimeout(t);
  }, [q, open, mode]);

  const track = (kind, item) => apiTrack(kind, item);

  const enterOracle = useCallback((seed) => {
    setMode("oracle");
    setQ(""); setResults(null);
    setTimeout(() => {
      inputRef.current?.focus();
      if (seed) oracleRef.current?.ask(seed);
    }, 30);
  }, []);

  /* flat list for keyboard navigation */
  const flat = useMemo(() => {
    if (wsOpen) return wsOpen.items.map((i, n) => ({ ...i, id: `ws:${n}`, group: wsOpen.name.toUpperCase() }));
    if (results) {
      const ctx = contextActionsFor(location.pathname);
      return [...(results.intents || []), ...ctx.filter((c) => c.title.toLowerCase().includes(q.toLowerCase())), ...(results.results || [])];
    }
    if (!dash) return [];
    const ctx = contextActionsFor(location.pathname);
    const lvl = dash.level || 0;
    const oracleRows = [];
    if (lvl >= 1) {
      oracleRows.push({ id: "oracle:inline", title: "Consult Oracle", group: "ORACLE",
                        icon: "sparkles", subtitle: "answers inline — ask anything" });
    }
    if (lvl >= 2) {
      oracleRows.push({ id: "oracle:brief", title: "Generate Founder Brief", group: "ORACLE",
                        icon: "sun", subtitle: "full operational briefing" });
      oracleRows.push({ id: "alerts:center", title: "Alert Center", group: "ORACLE",
                        icon: "shield-alert",
                        subtitle: alerts.length ? `${alerts.length} active alert${alerts.length !== 1 ? "s" : ""}` : "all signals nominal" });
    }
    const alertRows = alerts.slice(0, 3).map((a) => ({
      id: `alert:${a.id}`, title: a.title, group: "ORACLE ALERTS",
      icon: "shield-alert", subtitle: a.reason, url: a.url, severity: a.severity,
    }));
    const watchRows = watchlist.slice(0, 5).map((w) => ({
      id: `watched:${w.slug}`, title: w.title, group: "WATCHLIST", icon: "eye",
      subtitle: w.changes?.length ? w.changes.join(" · ") : "no changes since last review",
      url: w.url, watchSlug: w.slug, changed: !!w.changes?.length,
    }));
    const recRows = recs.slice(0, 4).map((r) => ({
      id: `rec:${r.id}`, title: r.title, group: "ORACLE RECOMMENDS",
      icon: r.icon || "zap", subtitle: (r.reasons || [r.detail])[0], url: r.url,
      severity: r.severity, rec: { id: r.id, metric: r.metric || 0 },
    }));
    return [
      ...(dash.pinned || []).map((p) => ({ ...p, group: "PINNED", pinned: true })),
      ...alertRows,
      ...recRows,
      ...oracleRows,
      ...ctx,
      ...watchRows,
      ...(dash.recent_used || []).map((r) => ({ ...r, group: "CONTINUE WHERE YOU LEFT OFF" })),
      ...(dash.recent || []),
      ...(dash.workspaces || []).map((w, n) => ({
        id: `workspace:${n}`, title: w.name, group: "WORKSPACES", icon: w.icon || "layers",
        subtitle: `${w.items.length} operations`, workspace: w,
      })),
      ...(lvl >= 1 ? [{ id: "workspace:manage", title: "Manage Workspaces",
                        group: "WORKSPACES", icon: "layers",
                        subtitle: "create · edit · delete" },
                      { id: "identity:callsign", title: "Change Callsign",
                        group: "IDENTITY", icon: "fingerprint",
                        subtitle: identity?.callsign
                          ? `operating as ${identity.callsign}`
                          : "choose your operational callsign" }] : []),
    ];
  }, [results, dash, recs, alerts, watchlist, wsOpen, location.pathname, q, identity]);

  const groups = useMemo(() => {
    const g = new Map();
    flat.forEach((r, i) => {
      if (!g.has(r.group)) g.set(r.group, []);
      g.get(r.group).push({ ...r, _idx: i });
    });
    return [...g.entries()];
  }, [flat]);

  const run = useCallback((item) => {
    if (!item) return;
    if (item.workspace) { setWsOpen(item.workspace); setSel(0); return; }
    if (item.id === "workspace:manage") { setMode("builder"); return; }
    if (item.id === "identity:callsign") { setMode("callsign"); return; }
    if (item.id === "alerts:center") { setMode("alerts"); return; }
    if (String(item.id).startsWith("alert:")) {
      apiTrack("use", item);
      api("/command/alerts/act", { method: "post",
        data: { id: String(item.id).slice(6), action: "read" } }).catch(() => {});
      setOpen(false);
      if (item.url) navigate(item.url);
      return;
    }
    if (item.id === "watch:toggle") {
      const slug = (location.pathname.match(/^\/topic\/([^/#?]+)/) || [])[1];
      if (slug) {
        api("/command/watchlist/toggle", { method: "post", data: { slug } })
          .then(() => api("/command/watchlist")
            .then((w) => setWatchlist(w.watchlist || [])))
          .catch(() => {});
      }
      return;
    }
    if (String(item.id).startsWith("watched:") && item.watchSlug) {
      if (item.changed) {
        api("/command/watchlist/ack", { method: "post",
          data: { slug: item.watchSlug } }).catch(() => {});
      }
      setOpen(false);
      navigate(item.url);
      return;
    }
    if (item.id === "oracle:inline") { enterOracle(null); return; }
    if (item.id === "oracle:brief") { enterOracle("Generate founder briefing"); return; }
    if (item.compareQuery) { apiTrack("use", item); enterOracle(item.compareQuery); return; }
    if (item.id === "oracle:ask") { track("use", item); enterOracle(q.trim()); return; }
    track("use", item);
    if (q.trim()) track("history", { q });
    setOpen(false);
    if (item.url) {
      if (item.url.startsWith("http")) window.open(item.url, "_blank");
      else navigate(item.url);
    }
  }, [navigate, q, enterOracle]);

  const onInputKey = (e) => {
    if (mode === "oracle") {
      if (e.key === "Enter") {
        e.preventDefault();
        const text = q.trim();
        if (text) { oracleRef.current?.ask(text); setQ(""); }
      }
      return;
    }
    const hist = dash?.history || [];
    if (e.key === "ArrowDown") { e.preventDefault(); setSel((s) => Math.min(s + 1, flat.length - 1)); }
    else if (e.key === "ArrowUp") {
      e.preventDefault();
      if (!q.trim() && hist.length && sel === 0) {
        const ni = Math.min(histIdx + 1, hist.length - 1);
        setHistIdx(ni); setQ(hist[hist.length - 1 - ni]);
      } else setSel((s) => Math.max(s - 1, 0));
    }
    else if (e.key === "Tab") { e.preventDefault(); setSel((s) => (e.shiftKey ? Math.max(s - 1, 0) : Math.min(s + 1, flat.length - 1))); }
    else if (e.key === "Enter") { e.preventDefault(); run(flat[sel]); }
  };

  useEffect(() => {
    listRef.current?.querySelector(`[data-idx="${sel}"]`)?.scrollIntoView({ block: "nearest" });
  }, [sel]);

  if (!open) return null;
  const c = dash?.counts || {};

  return (
    <div className="fixed inset-0 z-[300] flex items-start justify-center pt-[8vh] px-3"
         style={{ background: "rgba(4,3,2,0.72)", backdropFilter: "blur(6px)" }}
         data-testid="command-palette-overlay"
         onMouseDown={() => setOpen(false)}>
      <div className="w-full max-w-2xl bg-[#0c0a08] border border-[#2D2A26] shadow-[0_24px_80px_rgba(0,0,0,0.9)] animate-in fade-in slide-in-from-top-2 duration-150 flex flex-col max-h-[78vh]"
           data-testid="command-palette"
           onMouseDown={(e) => e.stopPropagation()}>
        {/* Header */}
        <div className="px-4 pt-3 pb-2 border-b border-[#D92D20]/30">
          <div className="flex items-center gap-2">
            <Terminal size={13} className="text-[#D92D20]" />
            <span className="font-mono text-[10px] uppercase tracking-[0.45em] text-[#E4DFD3]">
              {mode === "oracle" ? "Oracle — Secure Channel" : mode === "builder" ? "Workspace Builder" : mode === "callsign" ? "Identity" : mode === "alerts" ? "Alert Center" : "Archive Command"}
            </span>
            {(wsOpen || mode !== "search") && (
              <button onClick={() => { setWsOpen(null); setMode("search"); setTimeout(() => inputRef.current?.focus(), 10); }}
                      className="ml-auto font-mono text-[9px] uppercase text-[#7B7168] underline"
                      data-testid="command-ws-back">← back</button>
            )}
            <kbd className="ml-auto font-mono text-[8px] text-[#5A554C] border border-[#2D2A26] px-1.5 py-0.5">ESC</kbd>
          </div>
          <div className="font-mono text-[9px] text-[#5A554C] mt-1">
            {mode === "oracle"
              ? "Conversational intelligence — Oracle answers inside the palette."
              : mode === "builder"
                ? "Save reusable command workspaces."
                : mode === "callsign"
                  ? "Your operational identity across Oracle and Archive Command."
                  : mode === "alerts"
                    ? "Live intelligence signals — every alert carries its evidence."
                    : "Search dossiers, entities, documents, members, tools — or ask Oracle."}
          </div>
          {mode !== "builder" && mode !== "callsign" && mode !== "alerts" && (
          <div className="flex items-center gap-2 mt-2">
            <span className="font-mono text-[13px] text-[#D92D20]">&gt;</span>
            <input ref={inputRef} value={q}
                   onChange={(e) => { setQ(e.target.value); setHistIdx(-1); setWsOpen(null); }}
                   onKeyDown={onInputKey}
                   placeholder={mode === "oracle" ? "ask Oracle — press Enter to transmit…" : "type a query, command, or question…"}
                   data-testid="command-input"
                   className="flex-1 bg-transparent outline-none font-mono text-[14px] text-[#E4DFD3] placeholder:text-[#454038] caret-[#D92D20]" />
            {q && mode === "search" && !results && <Search size={12} className="text-[#5A554C] animate-pulse" />}
          </div>
          )}
        </div>

        {/* Results / Oracle channel / Workspace builder */}
        <div ref={listRef} className="overflow-y-auto flex-1 py-1" data-testid="command-results">
          {mode === "oracle" && (
            <OracleInline ref={oracleRef}
                          callsign={identity?.callsign}
                          onNavigate={(url) => { setOpen(false); navigate(url); }}
                          getContext={() => pageContextFor(location.pathname)} />
          )}
          {mode === "callsign" && (
            <CallsignEditor onDone={() => { setMode("search"); setTimeout(() => inputRef.current?.focus(), 10); }} />
          )}
          {mode === "alerts" && (
            <AlertCenter onNavigate={(url) => { setOpen(false); navigate(url); }} />
          )}
          {mode === "builder" && (
            <WorkspaceBuilder workspaces={dash?.workspaces || []}
                              onDone={(exit) => {
                                api("/command/dashboard").then((d) => setDash(d)).catch(() => {});
                                if (exit) { setMode("search"); setTimeout(() => inputRef.current?.focus(), 10); }
                              }} />
          )}
          {mode === "search" && (<>
          {!q.trim() && dash?.morning_greeting && (dash.level || 0) >= 2 && (
            <MorningGreeting callsign={identity?.callsign} dash={dash}
                             onOpenBrief={() => enterOracle("Generate founder briefing")}
                             onAskOracle={() => enterOracle(null)} />
          )}
          {flat.length === 0 && q.trim() && results && (
            <div className="px-4 py-6 font-mono text-[10px] text-[#5A554C]" data-testid="command-empty">
              // no records match — press Enter on "Ask Oracle" or refine the query
            </div>
          )}
          {groups.map(([group, rows]) => (
            <div key={group}>
              <div className="px-4 pt-2 pb-1 flex items-center gap-2">
                <span className="font-mono text-[8px] uppercase tracking-[0.35em] text-[#D92D20]">{group}</span>
                <span className="flex-1 h-px bg-[#D92D20]/20" />
              </div>
              {rows.map((r) => (
                <div key={r.id + String(r._idx)} data-idx={r._idx}
                     data-testid={`command-result-${r.id}`}
                     onMouseEnter={() => setSel(r._idx)}
                     onClick={() => run(r)}
                     className="px-4 py-1.5 flex items-center gap-3 cursor-pointer transition-colors"
                     style={{ background: sel === r._idx ? "#181008" : "transparent", borderLeft: sel === r._idx ? "2px solid #D92D20" : "2px solid transparent" }}>
                  <Icon name={r.icon} className={sel === r._idx ? "text-[#D92D20]" : "text-[#5A554C]"} />
                  <div className="min-w-0 flex-1">
                    <div className="font-mono text-[12px] text-[#E4DFD3] truncate">{r.title}</div>
                    {r.subtitle && <div className="font-mono text-[9px] text-[#5A554C] truncate">{r.subtitle}{r.updated_at ? ` · ${r.updated_at}` : ""}</div>}
                  </div>
                  {sel === r._idx && (r.actions || []).slice(0, 3).map((a) => (
                    <button key={a.label}
                            onClick={(e) => { e.stopPropagation(); run({ ...r, url: a.url }); }}
                            data-testid={`command-action-${a.label}`}
                            className="font-mono text-[8px] uppercase tracking-[0.15em] text-[#9FA8D4] border border-[#2D2A26] px-1.5 py-0.5 hover:border-[#D92D20]">
                      {a.label}
                    </button>
                  ))}
                  {sel === r._idx && r.rec && (
                    <>
                      <button onClick={(e) => {
                                e.stopPropagation();
                                api("/command/recommendations/act", { method: "post",
                                  data: { id: r.rec.id, action: "complete", metric: r.rec.metric } })
                                  .then(() => setRecs((rs) => rs.filter((x) => x.id !== r.rec.id)))
                                  .catch(() => {});
                              }}
                              title="Mark completed" data-testid={`rec-complete-${r.rec.id}`}
                              className="font-mono text-[8px] uppercase text-[#3FB950] border border-[#2D2A26] px-1.5 py-0.5 hover:border-[#3FB950]">
                        ✓
                      </button>
                      <button onClick={(e) => {
                                e.stopPropagation();
                                api("/command/recommendations/act", { method: "post",
                                  data: { id: r.rec.id, action: "dismiss", metric: r.rec.metric } })
                                  .then(() => setRecs((rs) => rs.filter((x) => x.id !== r.rec.id)))
                                  .catch(() => {});
                              }}
                              title="Dismiss" data-testid={`rec-dismiss-${r.rec.id}`}
                              className="font-mono text-[8px] uppercase text-[#5A554C] border border-[#2D2A26] px-1.5 py-0.5 hover:border-[#D92D20] hover:text-[#D92D20]">
                        ✕
                      </button>
                    </>
                  )}
                  {!r.workspace && r.id && !String(r.id).startsWith("ctx:") && (
                    <button onClick={(e) => {
                              e.stopPropagation();
                              track(r.pinned ? "unpin" : "pin", r);
                              setDash((d) => d && ({ ...d, pinned: r.pinned ? (d.pinned || []).filter((p) => p.id !== r.id) : [...(d.pinned || []), r] }));
                              if (results) setResults((res) => ({ ...res, results: res.results.map((x) => x.id === r.id ? { ...x, pinned: !r.pinned } : x) }));
                            }}
                            title={r.pinned ? "Unpin" : "Pin"}
                            data-testid={`command-pin-${r.id}`}
                            className={sel === r._idx ? "opacity-100" : "opacity-0"}>
                      <Pin size={11} className={r.pinned ? "text-[#E0A526]" : "text-[#5A554C]"} />
                    </button>
                  )}
                </div>
              ))}
            </div>
          ))}
          {!q.trim() && dash && (dash.pending_submissions > 0 || dash.security_alerts_24h > 0) && (
            <div className="px-4 py-2 flex flex-wrap gap-3 font-mono text-[9px]" data-testid="command-alerts">
              {dash.pending_submissions > 0 && (
                <span className="text-[#E0A526]">▲ {dash.pending_submissions} submissions awaiting review</span>
              )}
              {dash.security_alerts_24h > 0 && (
                <span className="text-[#D92D20]">▲ {dash.security_alerts_24h} failed access attempts (24h)</span>
              )}
            </div>
          )}
          </>)}
        </div>

        {/* Founder status bar */}
        <div className="px-4 py-2 border-t border-[#D92D20]/30 flex flex-wrap items-center gap-x-4 gap-y-1"
             data-testid="command-status-bar">
          <span className="font-mono text-[8px] uppercase tracking-[0.25em] text-[#5A554C]">Archive Index</span>
          {[["Dossiers", c.dossiers], ["Profiles", c.profiles], ["Entities", c.entities],
            ["Relationships", c.relationships], ["Documents", c.documents],
            ["Media", c.media]].map(([l, n]) => (
            <span key={l} className="font-mono text-[8px] text-[#7B7168]">
              <span className="text-[#C9C2B4]">{n ?? "—"}</span> {l}
            </span>
          ))}
          <span className="ml-auto font-mono text-[8px] text-[#3FB950]">Oracle Online ●</span>
          {identity?.callsign && (
            <span className="font-mono text-[8px] text-[#E0A526]" data-testid="command-callsign">
              ◈ {identity.callsign}
            </span>
          )}
          <span className="font-mono text-[8px] text-[#3FB950]">Search Indexed ●</span>
          {dash?.system_health && (
            <span className={`font-mono text-[8px] ${dash.system_health === "nominal" ? "text-[#3FB950]" : "text-[#E0A526]"}`}
                  data-testid="command-system-health">
              System {dash.system_health === "nominal" ? "Nominal" : "Attention"}
            </span>
          )}
          {dash?.last_brief_at && (
            <span className="font-mono text-[8px] text-[#7B7168]" data-testid="command-last-brief">
              Brief {dash.last_brief_at.slice(5, 16)}
            </span>
          )}
          {dash?.founder_verified != null && (
            <span className={`font-mono text-[8px] ${dash.founder_verified ? "text-[#3FB950]" : "text-[#E0A526]"}`}
                  data-testid="command-founder-verified">
              {dash.founder_verified ? "Founder Verified" : "Step-Up Required"}
            </span>
          )}
        </div>
      </div>
    </div>
  );
}
